import { useTranslation } from "react-i18next";
import { fullName } from "../../utils/helpers/userDisplay";
import { CountCell, LastVisitCell, PersonIdentity, isUncovered } from "./PersonTable";
import { META_LABEL, UNCOVERED_RAIL } from "./coverageStyles";

// The phone layout is a real card list, not the table with its columns squeezed
// to nothing: at 390px five columns put three characters each and the numbers
// stop being comparable, which is the only thing they are for.
//
// The two role groups sit SIDE BY SIDE as soon as they fit, because their
// adjacency is the whole point of the change — panels a scroll apart would
// rebuild the tab strip vertically and "who visits vs who is visited" would
// again be two screens.
//
// But only as soon as they FIT. A 390px phone gives the card about 150px per
// panel once the app's rail and the page gutter are out — checked in the
// browser, not calculated: «КАК НАБЛЮДАТЕЛЬ» wrapped to two lines and
// «⚠ просрочено 4» broke across one. Below 420px they stack, where the two
// panels are still one short scroll apart rather than one tab apart, which is
// the distinction that mattered.
function RolePanel({ label, uncovered, children }) {
  return (
    // `pl-4` on BOTH panels whether or not the rail is drawn: padding that
    // appears with the rail would shunt the figures of a flagged card 3px out
    // of line with every other card in the list.
    <div className="relative overflow-hidden rounded-xl bg-slate-50 py-2.5 pl-4 pr-3">
      {uncovered && (
        <span
          aria-hidden="true"
          className={`absolute left-0 top-0 h-full w-[3px] ${UNCOVERED_RAIL}`}
        />
      )}
      <p className={META_LABEL}>{label}</p>
      {children}
    </div>
  );
}

function LastLine({ at }) {
  const { t } = useTranslation();
  return (
    <div className="mt-2 border-t border-slate-200/70 pt-2">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
        {t("coverage.col.lastVisit")}
      </p>
      <div className="mt-0.5">
        <LastVisitCell at={at} />
      </div>
    </div>
  );
}

export default function PersonCards({ rows, onOpenVisits, flagUncovered = true }) {
  const { t } = useTranslation();

  return (
    <ul className="divide-y divide-slate-100">
      {rows.map((row) => {
        const person = row.person || {};
        const uncovered = flagUncovered && isUncovered(row);
        const name = fullName(person);
        return (
          <li key={person.uuid} className="px-5 py-4">
            <PersonIdentity
              person={person}
              onOpen={() => onOpenVisits(row)}
              avatarClass="h-10 w-10 text-xs"
              nameClass="text-sm"
            />

            {/* Stacked on a phone, side by side once there is room. Two panels
                across a 390px screen leave ~150px each, which wrapped «КАК
                НАБЛЮДАТЕЛЬ» onto two lines and broke «просрочено 4» across a
                line break — measured, not guessed. The comparison the 2-up was
                for only pays off when both panels fit on one line anyway. */}
            <div className="mt-3 grid grid-cols-1 gap-2 min-[420px]:grid-cols-2">
              <RolePanel label={t("coverage.group.observer")}>
                <div className="mt-1.5">
                  <CountCell
                    align="left"
                    value={row.conducted_total}
                    official={row.conducted_official}
                    peer={row.conducted_peer}
                    overdue={row.conducted_overdue}
                    onOpen={() => onOpenVisits(row, "conducted")}
                    ariaLabel={t("coverage.col.conductedAria", { name })}
                  />
                </div>
                <LastLine at={row.last_conducted_at} />
              </RolePanel>

              <RolePanel label={t("coverage.group.teacher")} uncovered={uncovered}>
                <div className="mt-1.5">
                  <CountCell
                    align="left"
                    value={row.received_total}
                    official={row.received_official}
                    peer={row.received_peer}
                    overdue={row.received_overdue}
                    uncovered={uncovered}
                    onOpen={() => onOpenVisits(row, "received")}
                    ariaLabel={t("coverage.col.receivedAria", { name })}
                  />
                </div>
                <LastLine at={row.last_received_at} />
              </RolePanel>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
